import React, {useEffect, useRef, useState} from "react";
import {toast} from "react-toastify";
import ApiDatabase from "server";
import UseContext from "hooks/useContext";
import useModal from "components/modal/useModal";
import {calendarDateToLocalDate, localDateToCalendarDate} from "helper/residencePermit";
import {convertPdfToImageIfNeeded} from "utils/pdfToImage";
import DocumentChoiceStep from "./DocumentChoiceStep";
import DocumentImportStep from "./DocumentImportStep";
import EndValidityStep from "./EndValidityStep";

const EMPTY_FILES = {fileR: null, fileV: null};

const KNOWN_ERROR_CODES = [
    "VISA_COMPLEMENTARY_PENDING",
    "RESIDENCE_PERMIT_MISSING",
    "VISA_COMPLEMENTARY_FILE_MISSING",
    "END_VALIDITY_IN_PAST",
    "INVALID_END_VALIDITY",
    "VISA_COMPLEMENTARY_NOT_DRAFT",
];

const toErrorKey = (error) => {
    const errorCode = error?.response?.data?.errorCode;
    return KNOWN_ERROR_CODES.includes(errorCode) ? errorCode : "default";
};

const ModalVisaExtension = () => {
    const {language, refreshLoadWorker} = UseContext();
    const {toggle} = useModal();

    const [step, setStep] = useState("choice");
    const [documentType, setDocumentType] = useState(null);
    const [files, setFiles] = useState(EMPTY_FILES);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadedDocument, setUploadedDocument] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [isConfirming, setIsConfirming] = useState(false);
    const [errorKey, setErrorKey] = useState(null);

    // The upload can last up to 45 s: the modal may be gone when the api answers.
    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const translate = (key) => language.getValue(`residencePermit.extension_modal.${key}`);
    const errorMessage = errorKey ? translate(`errors.${errorKey}`) : "";

    const needsBothSides = documentType === "new_permit";
    const canImport = Boolean(files.fileR) && (!needsBothSides || Boolean(files.fileV));

    const closeModal = () => {
        if (isUploading || isConfirming) return;
        toggle();
    };

    const selectDocumentType = (type) => {
        if (type !== documentType) setFiles(EMPTY_FILES);
        setDocumentType(type);
        setErrorKey(null);
        setStep("import");
    };

    const backToChoice = () => {
        if (isUploading) return;
        setErrorKey(null);
        setStep("choice");
    };

    const changeFile = (side, event) => {
        const file = event?.target?.files?.[0] || null;
        setFiles((previous) => ({...previous, [side]: file}));
        setErrorKey(null);
    };

    const failUpload = (key) => {
        if (!isMountedRef.current) return;
        setErrorKey(key);
        setIsUploading(false);
    };

    const importDocument = async () => {
        if (!canImport || isUploading) return;
        setErrorKey(null);
        setIsUploading(true);

        const formData = new FormData();
        try {
            // Receipt and ANEF attestation keep the whole PDF: prebuilt-read reads every page and the
            // admin must see the complete document. Only the new permit is converted (page 1 as image),
            // like the initial residence permit upload.
            const prepareFile = documentType === "new_permit" ? convertPdfToImageIfNeeded : async (file) => file;
            formData.append("type", documentType);
            formData.append("fileR", await prepareFile(files.fileR));
            if (needsBothSides) formData.append("fileV", await prepareFile(files.fileV));
        } catch (error) {
            failUpload("default");
            return;
        }

        ApiDatabase.uploadVisaComplementaryDocument(
            formData,
            (data) => {
                if (!isMountedRef.current) return;
                const document = data?.document;
                if (!document?._id) {
                    failUpload("default");
                    return;
                }
                setUploadedDocument(document);
                setEndDate(calendarDateToLocalDate(document.end_validity_ai));
                setIsUploading(false);
                setStep("date");
            },
            (error) => failUpload(toErrorKey(error))
        );
    };

    const changeEndDate = (date) => {
        setEndDate(date);
        setErrorKey(null);
    };

    const confirmEndValidity = () => {
        const endValidity = localDateToCalendarDate(endDate);
        if (!endValidity || !uploadedDocument?._id || isConfirming) return;
        setErrorKey(null);
        setIsConfirming(true);

        ApiDatabase.confirmVisaComplementaryDocument(
            {documentId: uploadedDocument._id, end_validity: endValidity},
            () => {
                toast.success(translate("success"));
                refreshLoadWorker();
                if (isMountedRef.current) toggle();
            },
            (error) => {
                if (!isMountedRef.current) return;
                setErrorKey(toErrorKey(error));
                setIsConfirming(false);
            }
        );
    };

    if (step === "import" && documentType) {
        return (
            <DocumentImportStep
                translate={translate}
                documentType={documentType}
                files={files}
                onFileChange={changeFile}
                canImport={canImport}
                isUploading={isUploading}
                errorMessage={errorMessage}
                onBack={backToChoice}
                onClose={closeModal}
                onImport={importDocument}
            />
        );
    }

    if (step === "date") {
        return (
            <EndValidityStep
                translate={translate}
                endDate={endDate}
                onEndDateChange={changeEndDate}
                wasDateRead={Boolean(calendarDateToLocalDate(uploadedDocument?.end_validity_ai))}
                isConfirming={isConfirming}
                errorMessage={errorMessage}
                onClose={closeModal}
                onConfirm={confirmEndValidity}
            />
        );
    }

    return (
        <DocumentChoiceStep
            translate={translate}
            onClose={closeModal}
            onSelect={selectDocumentType}
        />
    );
};

export default ModalVisaExtension;
